/**
 * CD-ICONS-01 — the 98 ratified glyphs, as data.
 *
 * ⛔ GENERATED, NOT AUTHORED. Every field below is copied verbatim from
 * `.claude/references/design/icon-system/icons.json` — the founder-ratified
 * source of truth (2026-07-26: 36/36 base rows picked with zero overrides,
 * plus founder-ratified Addendum A1). The house rule for
 * this session is "picked assets ship exactly": paths are NOT redrawn, cleaned
 * up, optimised, re-numbered or substituted for a library's.
 *
 * That count is the 36 base picks (2026-07-26, zero overrides) plus Addendum
 * A1's three (`nav.train`, `nav.runs`, `sys.trash`) and the rest of icons.json,
 * plus the wave 2 Addendum A3 (`nav.week`, read from
 * `.claude/references/design/icon-system/addendum-a3/icons.addendum.json` and
 * appended last). It is written by the generator rather than typed — so it
 * cannot go stale the way a hand-kept number does.
 *
 * `registry.fidelity.test.ts` re-reads icons.json from disk and asserts every
 * `d`, `f`, `dash`, `vb` and `stroke` here is byte-equal to it — one pin per
 * icon — and pins the A3 rows against their own file, so a well-meaning
 * "tidy-up" of a path string fails the suite rather than silently shipping
 * different art.
 *
 * Colour lives nowhere in this file. Everything renders `currentColor`.
 * (R1, CD-ICONS-02: the once-ratified unread DOT was retired — badge-only;
 * no glyph-level colour exception exists. `--signal` appears in product only
 * as the notification badge background, bound at its surface to `tokens.css`.)
 */

/** One path of a glyph. */
export interface IconPathDef {
  /** SVG path data, verbatim from icons.json. */
  d: string;
  /** `1` = fill `currentColor`, no stroke (icons.json `f`). */
  f?: 1;
  /** Maps to `stroke-dasharray` (icons.json `dash`). */
  dash?: string;
}

export interface IconDef {
  /** The ratified art name (e.g. "Flame"). Not a UI string. */
  name: string;
  /** viewBox edge: 24 for glyphs, 48 for `spot.*`. */
  vb: number;
  /** Per-icon stroke width override; absent = the 1.5 direction default. */
  stroke?: number;
  /** The sizes the ratification lists for this icon. */
  sizes: readonly number[];
  paths: readonly IconPathDef[];
}

/** The art, with literal keys so `IconName` derives from it. */
const ICONS_RAW = {
  "kudo.fire": {
    name: "Flame",
    vb: 24,
    sizes: [16, 20, 48],
    paths: [
      { d: "M12 3.5c2.6 2.7 5 5.8 5 9a5 5 0 0 1-10 0c0-1.4.4-2.7 1.2-4 .7 1.1 1.6 1.8 2.6 2.2-.3-2.4.2-4.8 1.2-7.2z" },
    ],
  },
  "kudo.beast": {
    name: "Dumbbell",
    vb: 24,
    sizes: [16, 20, 48],
    paths: [
      { d: "M4 9.5v5" },
      { d: "M7 7.5v9" },
      { d: "M17 7.5v9" },
      { d: "M20 9.5v5" },
      { d: "M7 12h10" },
    ],
  },
  "kudo.trophy": {
    name: "Summit",
    vb: 24,
    sizes: [16, 20, 48],
    paths: [
      { d: "M3.75 19 7.8 11.5l2.3 3.55L14 8.75 20.25 19z" },
      { d: "M14 8.75V3.75" },
      { d: "M14 3.75h4.2l-1.4 1.65 1.4 1.65H14" },
    ],
  },
  "kudo.star": {
    name: "Sparkle",
    vb: 24,
    sizes: [16, 20, 48],
    paths: [
      { d: "M12 3.5c.6 4.55 3.35 7.3 8.5 8.5-5.15 1.2-7.9 3.95-8.5 8.5-.6-4.55-3.35-7.3-8.5-8.5 5.15-1.2 7.9-3.95 8.5-8.5z" },
    ],
  },
  "kudo.heart": {
    name: "Heart",
    vb: 24,
    sizes: [16, 20, 48],
    paths: [
      { d: "M12 19.4C7.85 16.7 3.5 13.15 3.5 8.95 3.5 6.35 5.5 4.5 7.8 4.5c1.7 0 3.2 1 4.2 2.7 1-1.7 2.5-2.7 4.2-2.7 2.3 0 4.3 1.85 4.3 4.45 0 4.2-4.35 7.75-8.5 10.45z" },
    ],
  },
  "kudo.lightning": {
    name: "Bolt",
    vb: 24,
    sizes: [16, 20, 48],
    paths: [
      { d: "M13.2 3.5 5.6 13.4h4.9l-1.7 7.1 7.6-9.9h-4.9z" },
    ],
  },
  "nav.today": {
    name: "Today (calendar-day)",
    vb: 24,
    sizes: [24],
    paths: [
      { d: "M6.75 5.5h10.5a2.25 2.25 0 0 1 2.25 2.25v9.5a2.25 2.25 0 0 1-2.25 2.25H6.75a2.25 2.25 0 0 1-2.25-2.25v-9.5A2.25 2.25 0 0 1 6.75 5.5z" },
      { d: "M8.5 3.25V7" },
      { d: "M15.5 3.25V7" },
      { d: "M4.5 10.25h15" },
      { d: "M10.65 14.9a1.35 1.35 0 1 0 2.7 0a1.35 1.35 0 1 0 -2.7 0", f: 1 },
    ],
  },
  "nav.team": {
    name: "Athletes",
    vb: 24,
    sizes: [24],
    paths: [
      { d: "M6.1499999999999995 7.55a3.05 3.05 0 1 0 6.1 0a3.05 3.05 0 1 0 -6.1 0" },
      { d: "M3.5 19.6v-1.05a4.7 4.7 0 0 1 4.7-4.7h2a4.7 4.7 0 0 1 4.7 4.7v1.05" },
      { d: "M15.7 4.8a3.05 3.05 0 0 1 0 5.5" },
      { d: "M20.5 19.6v-1.05a4.7 4.7 0 0 0-3.3-4.48" },
    ],
  },
  "nav.chat": {
    name: "Bubble",
    vb: 24,
    sizes: [24],
    paths: [
      { d: "M19.75 13.9a2.1 2.1 0 0 1-2.1 2.1H9.1l-4.85 3.75V6.6a2.1 2.1 0 0 1 2.1-2.1h11.3a2.1 2.1 0 0 1 2.1 2.1z" },
    ],
  },
  "nav.races": {
    name: "Pennant",
    vb: 24,
    sizes: [24],
    paths: [
      { d: "M6 20.75V3.5" },
      { d: "M6 4.5h11.6l-2.7 3.6 2.7 3.6H6" },
    ],
  },
  "nav.settings": {
    name: "Sliders",
    vb: 24,
    sizes: [24],
    paths: [
      { d: "M4 7.4h7.45" },
      { d: "M11.45 7.4a2.05 2.05 0 1 0 4.1 0a2.05 2.05 0 1 0 -4.1 0" },
      { d: "M15.55 7.4H20" },
      { d: "M4 12h1.95" },
      { d: "M5.95 12a2.05 2.05 0 1 0 4.1 0a2.05 2.05 0 1 0 -4.1 0" },
      { d: "M10.05 12H20" },
      { d: "M4 16.6h9.45" },
      { d: "M13.45 16.6a2.05 2.05 0 1 0 4.1 0a2.05 2.05 0 1 0 -4.1 0" },
      { d: "M17.55 16.6H20" },
    ],
  },
  "nav.notify": {
    name: "Bell",
    vb: 24,
    sizes: [20, 24],
    paths: [
      { d: "M6.4 9.1a5.6 5.6 0 0 1 11.2 0c0 3.3.65 5.1 1.45 6.25.35.5 0 1.25-.65 1.25H5.6c-.65 0-1-.75-.65-1.25.8-1.15 1.45-2.95 1.45-6.25z" },
      { d: "M9.6 19.9a2.4 2.4 0 0 0 4.8 0" },
    ],
  },
  "nav.active": {
    name: "Active treatment: dot under",
    vb: 24,
    sizes: [24],
    paths: [
      { d: "M10.7 22.1a1.3 1.3 0 1 0 2.6 0a1.3 1.3 0 1 0 -2.6 0", f: 1 },
    ],
  },
  "chip.streak": {
    name: "Flame (alias)",
    vb: 24,
    sizes: [16],
    paths: [
      { d: "M12 3.5c2.6 2.7 5 5.8 5 9a5 5 0 0 1-10 0c0-1.4.4-2.7 1.2-4 .7 1.1 1.6 1.8 2.6 2.2-.3-2.4.2-4.8 1.2-7.2z" },
    ],
  },
  "chip.reward": {
    name: "Token",
    vb: 24,
    sizes: [16],
    paths: [
      { d: "M4.25 12a7.75 7.75 0 1 0 15.5 0a7.75 7.75 0 1 0 -15.5 0" },
      { d: "m12 8.9.95 1.95 2.15.3-1.55 1.5.35 2.15L12 13.8l-1.9 1 .35-2.15-1.55-1.5 2.15-.3z", f: 1 },
    ],
  },
  "mark.celebrate": {
    name: "Burst",
    vb: 24,
    sizes: [48],
    paths: [
      { d: "M12 3.4v3" },
      { d: "M12 17.6v3" },
      { d: "M3.4 12h3" },
      { d: "M17.6 12h3" },
      { d: "M5.9 5.9 8 8" },
      { d: "M16 16l2.1 2.1" },
      { d: "M18.1 5.9 16 8" },
      { d: "M8 16l-2.1 2.1" },
    ],
  },
  "sys.send": {
    name: "Plane",
    vb: 24,
    sizes: [20],
    paths: [
      { d: "M20.3 3.7 3.9 10.4l6.7 2.9 2.9 6.7 6.8-16.3z" },
      { d: "M20.3 3.7 10.6 13.3" },
    ],
  },
  "sys.plus": {
    name: "Plus",
    vb: 24,
    sizes: [20],
    paths: [
      { d: "M12 5.4v13.2" },
      { d: "M5.4 12h13.2" },
    ],
  },
  "sys.close": {
    name: "Close",
    vb: 24,
    sizes: [20],
    paths: [
      { d: "M6.4 6.4 17.6 17.6" },
      { d: "M17.6 6.4 6.4 17.6" },
    ],
  },
  "sys.search": {
    name: "Search",
    vb: 24,
    sizes: [20],
    paths: [
      { d: "M5.299999999999999 11.2a5.9 5.9 0 1 0 11.8 0a5.9 5.9 0 1 0 -11.8 0" },
      { d: "M15.6 15.6l4.7 4.7" },
    ],
  },
  "sys.chevron": {
    name: "Chevron (right; rotate for l/u/d)",
    vb: 24,
    sizes: [16, 20],
    paths: [
      { d: "M9.2 5.6 15.6 12l-6.4 6.4" },
    ],
  },
  "sys.more": {
    name: "More (horizontal)",
    vb: 24,
    sizes: [20],
    paths: [
      { d: "M4.1499999999999995 12a1.45 1.45 0 1 0 2.9 0a1.45 1.45 0 1 0 -2.9 0", f: 1 },
      { d: "M10.55 12a1.45 1.45 0 1 0 2.9 0a1.45 1.45 0 1 0 -2.9 0", f: 1 },
      { d: "M16.95 12a1.45 1.45 0 1 0 2.9 0a1.45 1.45 0 1 0 -2.9 0", f: 1 },
    ],
  },
  "sys.check": {
    name: "Check",
    vb: 24,
    sizes: [16, 20],
    paths: [
      { d: "M4.9 12.9l4.3 4.3L19.1 7.1" },
    ],
  },
  "sys.check-circle": {
    name: "Check, ringed",
    vb: 24,
    sizes: [20],
    paths: [
      { d: "M4.1 12a7.9 7.9 0 1 0 15.8 0a7.9 7.9 0 1 0 -15.8 0" },
      { d: "M8.5 12.4l2.5 2.5 4.7-5.2" },
    ],
  },
  "sys.edit": {
    name: "Pencil",
    vb: 24,
    sizes: [16, 20],
    paths: [
      { d: "M14.7 5.3l4 4L8.4 19.6l-4.9.9.9-4.9z" },
      { d: "M12.9 7.1l4 4" },
    ],
  },
  "sys.calendar": {
    name: "Calendar",
    vb: 24,
    sizes: [16, 20],
    paths: [
      { d: "M6.75 5.5h10.5a2.25 2.25 0 0 1 2.25 2.25v9.5a2.25 2.25 0 0 1-2.25 2.25H6.75a2.25 2.25 0 0 1-2.25-2.25v-9.5A2.25 2.25 0 0 1 6.75 5.5z" },
      { d: "M8.5 3.25V7" },
      { d: "M15.5 3.25V7" },
      { d: "M4.5 10.25h15" },
    ],
  },
  "sys.clock": {
    name: "Clock",
    vb: 24,
    sizes: [16, 20],
    paths: [
      { d: "M4.1 12a7.9 7.9 0 1 0 15.8 0a7.9 7.9 0 1 0 -15.8 0" },
      { d: "M12 7.6V12l3.1 1.85" },
    ],
  },
  "sys.pin": {
    name: "Pin",
    vb: 24,
    sizes: [16, 20],
    paths: [
      { d: "M12 20.9s-6.6-5.35-6.6-10.15a6.6 6.6 0 0 1 13.2 0C18.6 15.55 12 20.9 12 20.9z" },
      { d: "M9.75 10.55a2.25 2.25 0 1 0 4.5 0a2.25 2.25 0 1 0 -4.5 0" },
    ],
  },
  "sys.info": {
    name: "Info",
    vb: 24,
    sizes: [16, 20],
    paths: [
      { d: "M4.1 12a7.9 7.9 0 1 0 15.8 0a7.9 7.9 0 1 0 -15.8 0" },
      { d: "M12 11.2v5" },
      { d: "M10.9 7.9a1.1 1.1 0 1 0 2.2 0a1.1 1.1 0 1 0 -2.2 0", f: 1 },
    ],
  },
  "sys.warn": {
    name: "Warn",
    vb: 24,
    sizes: [20],
    paths: [
      { d: "M10.4 4.5c.7-1.25 2.5-1.25 3.2 0l7.05 12.2c.7 1.2-.17 2.7-1.6 2.7H4.95c-1.43 0-2.3-1.5-1.6-2.7z" },
      { d: "M12 9.4v4.2" },
      { d: "M10.9 16.5a1.1 1.1 0 1 0 2.2 0a1.1 1.1 0 1 0 -2.2 0", f: 1 },
    ],
  },
  "sys.camera": {
    name: "Camera",
    vb: 24,
    sizes: [20],
    paths: [
      { d: "M4.4 8.7c0-.9.75-1.65 1.65-1.65h2.2L9.8 4.9h4.4l1.55 2.15h2.2c.9 0 1.65.75 1.65 1.65v8.4c0 .9-.75 1.65-1.65 1.65H6.05c-.9 0-1.65-.75-1.65-1.65z" },
      { d: "M8.7 12.7a3.3 3.3 0 1 0 6.6 0a3.3 3.3 0 1 0 -6.6 0" },
    ],
  },
  "sys.journey": {
    name: "Journey",
    vb: 24,
    sizes: [16, 20],
    paths: [
      { d: "M4.6 19.4c5.3 0 4.05-6.4 7.9-6.4 3.6 0 2.75-6.4 6.9-6.4" },
      { d: "M3.0999999999999996 19.4a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0 -3 0", f: 1 },
      { d: "M17.5 6.6a1.9 1.9 0 1 0 3.8 0a1.9 1.9 0 1 0 -3.8 0" },
    ],
  },
  "sys.sign-out": {
    name: "Sign out",
    vb: 24,
    sizes: [20],
    paths: [
      { d: "M9.4 4.5H6.1c-.9 0-1.6.7-1.6 1.6v11.8c0 .9.7 1.6 1.6 1.6h3.3" },
      { d: "M15.1 15.6 18.7 12l-3.6-3.6" },
      { d: "M18.7 12H9.6" },
    ],
  },
  "spot.empty-chat": {
    name: "First word",
    vb: 48,
    stroke: 2,
    sizes: [48],
    paths: [
      { d: "M39.5 26.5a4 4 0 0 1-4 4H19l-8.5 6.5V12.5a4 4 0 0 1 4-4h21a4 4 0 0 1 4 4z" },
      { d: "M36.5 35.5c.5 3.2 2.3 5 5.5 5.5-3.2.5-5 2.3-5.5 5.5-.5-3.2-2.3-5-5.5-5.5 3.2-.5 5-2.3 5.5-5.5z" },
      { d: "M17.3 19.5a1.7 1.7 0 1 0 3.4 0a1.7 1.7 0 1 0 -3.4 0", f: 1 },
      { d: "M23.3 19.5a1.7 1.7 0 1 0 3.4 0a1.7 1.7 0 1 0 -3.4 0", f: 1 },
      { d: "M29.3 19.5a1.7 1.7 0 1 0 3.4 0a1.7 1.7 0 1 0 -3.4 0", f: 1 },
    ],
  },
  "spot.empty-team": {
    name: "The week ahead",
    vb: 48,
    stroke: 2,
    sizes: [48],
    paths: [
      { d: "M3 24a3 3 0 1 0 6 0a3 3 0 1 0 -6 0", f: 1 },
      { d: "M12 24a3 3 0 1 0 6 0a3 3 0 1 0 -6 0", f: 1 },
      { d: "M21 24a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" },
      { d: "M21 24a3 3 0 0 0 6 0z", f: 1 },
      { d: "M30 24a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" },
      { d: "M39 24a3 3 0 1 0 6 0a3 3 0 1 0 -6 0", dash: "2 2.8" },
    ],
  },
  "spot.empty-races": {
    name: "Course open",
    vb: 48,
    stroke: 2,
    sizes: [48],
    paths: [
      { d: "M10.5 41.5V8.4" },
      { d: "M10.5 9.8c2.5-1.7 5-2.6 7.6-2.6 4.9 0 7.3 2.9 12.2 2.9 2.6 0 5.1-.8 7.2-2.3v17.2c-2.1 1.5-4.6 2.3-7.2 2.3-4.9 0-7.3-2.9-12.2-2.9-2.6 0-5.1.9-7.6 2.6z" },
      { d: "M18 41.5h4" },
      { d: "M26 41.5h4" },
      { d: "M34 41.5h4" },
    ],
  },
  "nav.train": {
    name: "Track",
    vb: 24,
    sizes: [22, 24],
    paths: [
      { d: "M3.5 12a8.5 5.25 0 1 0 17 0a8.5 5.25 0 1 0-17 0" },
      { d: "M10.5 17.25a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0 -3 0", f: 1 },
    ],
  },
  "nav.runs": {
    name: "Stopwatch",
    vb: 24,
    sizes: [22, 24],
    paths: [
      { d: "M5.4 13.6a6.6 6.6 0 1 0 13.2 0a6.6 6.6 0 1 0 -13.2 0" },
      { d: "M10.2 3.25h3.6" },
      { d: "M12 3.25v2.7" },
      { d: "M12 13.6l2.9-2.9" },
    ],
  },
  "sys.trash": {
    name: "Bin",
    vb: 24,
    sizes: [16, 20],
    paths: [
      { d: "M5.5 7h13" },
      { d: "M9.5 7V5.2c0-.4.3-.7.7-.7h3.6c.4 0 .7.3.7.7V7" },
      { d: "M7 7l.8 12.1c.05.8.72 1.4 1.5 1.4h5.4c.78 0 1.45-.6 1.5-1.4L17 7" },
      { d: "M10.2 10.5v6" },
      { d: "M13.8 10.5v6" },
    ],
  },
  "nav.guardian-home": {
    name: "Home",
    vb: 24,
    sizes: [22, 24],
    paths: [
      { d: "M4.75 11.25 12 4.75l7.25 6.5" },
      { d: "M6.5 10.4v7.85a1.5 1.5 0 0 0 1.5 1.5h8a1.5 1.5 0 0 0 1.5-1.5V10.4" },
      { d: "M10.25 19.75v-3.9a1.75 1.75 0 0 1 3.5 0v3.9" },
    ],
  },
  "nav.approvals": {
    name: "Inbox",
    vb: 24,
    sizes: [22, 24],
    paths: [
      { d: "M12 3.9v6.3" },
      { d: "M9.3 7.5 12 10.2l2.7-2.7" },
      { d: "M4.75 13.9h3.85l1.6 2.5h3.6l1.6-2.5h3.85" },
      { d: "M4.75 13.9v3.6a1.75 1.75 0 0 0 1.75 1.75h11a1.75 1.75 0 0 0 1.75-1.75v-3.6" },
    ],
  },
  "sys.admin": {
    name: "Shield",
    vb: 24,
    sizes: [16],
    paths: [
      { d: "M12 3.75c2.35 1.3 4.7 2 7.25 2.15C19 12.4 16.75 16.9 12 20.25 7.25 16.9 5 12.4 4.75 5.9 7.3 5.75 9.65 5.05 12 3.75z" },
    ],
  },
  "sys.role-swap": {
    name: "Swap",
    vb: 24,
    sizes: [16],
    paths: [
      { d: "M5 8.25h11.25" },
      { d: "M13.5 5.5 16.25 8.25 13.5 11" },
      { d: "M19 15.75H7.75" },
      { d: "M10.5 13 7.75 15.75l2.75 2.75" },
    ],
  },
  "sys.bug": {
    name: "Bug",
    vb: 24,
    sizes: [16, 20],
    paths: [
      { d: "M6.6 13.4a5.4 5.4 0 1 0 10.8 0a5.4 5.4 0 1 0 -10.8 0" },
      { d: "M12 8v10.8" },
      { d: "M9.9 8.6 8.35 6.2" },
      { d: "M14.1 8.6l1.55-2.4" },
      { d: "M6.6 11.9H4.25" },
      { d: "M6.9 15.6l-2.15 1.05" },
      { d: "M17.4 11.9h2.35" },
      { d: "M17.1 15.6l2.15 1.05" },
    ],
  },
  "phase.base": {
    name: "Strata",
    vb: 24,
    sizes: [14, 16, 20],
    paths: [
      { d: "M4.75 18.5h14.5" },
      { d: "M6.9 14.5h10.2" },
      { d: "M9.05 10.5h5.9" },
      { d: "M11.2 6.5h1.6" },
    ],
  },
  "phase.build": {
    name: "Stairs",
    vb: 24,
    sizes: [14, 16, 20],
    paths: [
      { d: "M4.5 19.25h4.4v-4.4h4.4v-4.4h4.4v-4.4h1.95" },
    ],
  },
  "phase.competition": {
    name: "Bib, numbered",
    vb: 24,
    sizes: [14, 16, 20],
    paths: [
      { d: "M6.5 5.75h11a1.5 1.5 0 0 1 1.5 1.5v9.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 16.75v-9.5a1.5 1.5 0 0 1 1.5-1.5z" },
      { d: "M8.6 4.5v2.5" },
      { d: "M15.4 4.5v2.5" },
      { d: "M9.25 11.4h5.5" },
      { d: "M9.25 14.6h5.5" },
    ],
  },
  "phase.taper": {
    name: "Target",
    vb: 24,
    sizes: [14, 16, 20],
    paths: [
      { d: "M4.4 12a7.6 7.6 0 1 0 15.2 0a7.6 7.6 0 1 0 -15.2 0" },
      { d: "M7.9 12a4.1 4.1 0 1 0 8.2 0a4.1 4.1 0 1 0 -8.2 0" },
      { d: "M10.7 12a1.3 1.3 0 1 0 2.6 0a1.3 1.3 0 1 0 -2.6 0", f: 1 },
    ],
  },
  "phase.recovery": {
    name: "Moon",
    vb: 24,
    sizes: [14, 16, 20],
    paths: [
      { d: "M12 3.4a5.9 5.9 0 0 0 8.85 8.85A8.85 8.85 0 1 1 12 3.4z" },
    ],
  },
  "mark.meet": {
    name: "Meet flag",
    vb: 24,
    sizes: [16, 12, 9],
    paths: [
      { d: "M7.25 4.75v14.5" },
      { d: "M7.25 5.4h9.3l-2.4 3.15 2.4 3.15h-9.3z", f: 1 },
    ],
  },
  "sys.circle": {
    name: "Open dot",
    vb: 24,
    sizes: [12, 16],
    paths: [
      { d: "M4.5 12a7.5 7.5 0 1 0 15 0a7.5 7.5 0 1 0 -15 0" },
    ],
  },
  "sys.bang": {
    name: "Bang",
    vb: 24,
    sizes: [12, 16],
    paths: [
      { d: "M12 5v8.4" },
      { d: "M10.7 17.6a1.3 1.3 0 1 0 2.6 0a1.3 1.3 0 1 0 -2.6 0", f: 1 },
    ],
  },
  "journey.competitive": {
    name: "Medal",
    vb: 24,
    sizes: [24],
    paths: [
      { d: "M8.9 4.75l1.5 4.75" },
      { d: "M15.1 4.75l-1.5 4.75" },
      { d: "M7.5 13.75a4.5 4.5 0 1 0 9 0a4.5 4.5 0 1 0 -9 0" },
    ],
  },
  "journey.competitive-team": {
    name: "Baton",
    vb: 24,
    sizes: [24],
    paths: [
      { d: "M4.9 15.9 15.9 4.9a1.55 1.55 0 0 1 2.2 2.2l-11 11a1.55 1.55 0 0 1-2.2-2.2z" },
    ],
  },
  "journey.time": {
    name: "Clock (alias)",
    vb: 24,
    sizes: [24],
    paths: [
      { d: "M4.1 12a7.9 7.9 0 1 0 15.8 0a7.9 7.9 0 1 0 -15.8 0" },
      { d: "M12 7.6V12l3.1 1.85" },
    ],
  },
  "journey.health": {
    name: "Heart, moving",
    vb: 24,
    sizes: [24],
    paths: [
      { d: "M12 19.4C7.85 16.7 3.5 13.15 3.5 8.95 3.5 6.35 5.5 4.5 7.8 4.5c1.7 0 3.2 1 4.2 2.7 1-1.7 2.5-2.7 4.2-2.7 2.3 0 4.3 1.85 4.3 4.45 0 4.2-4.35 7.75-8.5 10.45z" },
      { d: "M8.4 11.9h1.7l1.1-2.1 1.5 3.9 1.1-2.1h1.8" },
    ],
  },
  "journey.social": {
    name: "Together",
    vb: 24,
    sizes: [24],
    paths: [
      { d: "M4.8500000000000005 12a4.55 4.55 0 1 0 9.1 0a4.55 4.55 0 1 0 -9.1 0" },
      { d: "M10.05 12a4.55 4.55 0 1 0 9.1 0a4.55 4.55 0 1 0 -9.1 0" },
    ],
  },
  "notif.kudo": {
    name: "Burst, small",
    vb: 24,
    sizes: [18, 20],
    paths: [
      { d: "M10.3 12a1.7 1.7 0 1 0 3.4 0a1.7 1.7 0 1 0 -3.4 0", f: 1 },
      { d: "M12 4.5v2.6" },
      { d: "M12 16.9v2.6" },
      { d: "M4.5 12h2.6" },
      { d: "M16.9 12h2.6" },
      { d: "M6.7 6.7l1.85 1.85" },
      { d: "M15.45 15.45l1.85 1.85" },
      { d: "M17.3 6.7l-1.85 1.85" },
      { d: "M8.55 15.45 6.7 17.3" },
    ],
  },
  "notif.joined": {
    name: "New name",
    vb: 24,
    sizes: [18, 20],
    paths: [
      { d: "M6.7 8.3a3.05 3.05 0 1 0 6.1 0a3.05 3.05 0 1 0 -6.1 0" },
      { d: "M4.25 19.5v-.85a4.9 4.9 0 0 1 4.9-4.9h1.2a4.9 4.9 0 0 1 4.9 4.9v.85" },
      { d: "M18.3 9.4v4.9" },
      { d: "M15.85 11.85h4.9" },
    ],
  },
  "notif.motivate": {
    name: "Sunrise",
    vb: 24,
    sizes: [18, 20],
    paths: [
      { d: "M4 17.4h16" },
      { d: "M7.85 17.4a4.15 4.15 0 0 1 8.3 0" },
      { d: "M12 9.4V6.9" },
      { d: "M6.3 12.6 4.75 11.05" },
      { d: "M17.7 12.6l1.55-1.55" },
    ],
  },
  "notif.waiting": {
    name: "Hourglass",
    vb: 24,
    sizes: [18, 20],
    paths: [
      { d: "M7.1 4.75h9.8" },
      { d: "M7.1 19.25h9.8" },
      { d: "M8.55 4.75v2.2c0 2.7 3.45 3.1 3.45 4.3s-3.45 1.6-3.45 4.3v2.2" },
      { d: "M15.45 4.75v2.2c0 2.7-3.45 3.1-3.45 4.3s3.45 1.6 3.45 4.3v2.2" },
    ],
  },
  "notif.invite": {
    name: "Envelope",
    vb: 24,
    sizes: [18, 20],
    paths: [
      { d: "M6.15 6.9h11.7a1.65 1.65 0 0 1 1.65 1.65v6.9a1.65 1.65 0 0 1-1.65 1.65H6.15A1.65 1.65 0 0 1 4.5 15.45v-6.9A1.65 1.65 0 0 1 6.15 6.9z" },
      { d: "M5.1 8.1l6.9 5 6.9-5" },
    ],
  },
  "spot.empty-notify": {
    name: "All quiet",
    vb: 48,
    stroke: 2,
    sizes: [48],
    paths: [
      { d: "M15 19.5a9 9 0 0 1 18 0c0 5.4 1.05 8.3 2.35 10.2.6.85 0 2.05-1.05 2.05H13.7c-1.05 0-1.65-1.2-1.05-2.05C13.95 27.8 15 24.9 15 19.5z" },
      { d: "M20.1 35.4a3.9 3.9 0 0 0 7.8 0" },
      { d: "M20.3 20.8l2.8 2.8 4.7-5.3" },
    ],
  },
  "spot.empty-meets": {
    name: "Season open",
    vb: 48,
    stroke: 2,
    sizes: [48],
    paths: [
      { d: "M13 10.5h22a4 4 0 0 1 4 4V34a4 4 0 0 1-4 4H13a4 4 0 0 1-4-4V14.5a4 4 0 0 1 4-4z" },
      { d: "M17 6.75V13.25" },
      { d: "M31 6.75V13.25" },
      { d: "M9 19.5h30" },
      { d: "M21.75 25v8.4" },
      { d: "M21.75 25.4h6.3l-1.65 2.05 1.65 2.05h-6.3" },
    ],
  },
  "spot.empty-approvals": {
    name: "Clear tray",
    vb: 48,
    stroke: 2,
    sizes: [48, 26],
    paths: [
      { d: "M12.05 13.3h23.9a2 2 0 0 1 1.9 1.4L41.5 26.5V35a3.5 3.5 0 0 1-3.5 3.5H10A3.5 3.5 0 0 1 6.5 35v-8.5l3.65-11.8a2 2 0 0 1 1.9-1.4z" },
      { d: "M6.5 26.5h9.4l2.4 3.7h7.4l2.4-3.7h9.3" },
    ],
  },
  "sys.caret": {
    name: "Caret",
    vb: 24,
    sizes: [11, 12, 14, 16],
    paths: [
      { d: "M6.75 9.75 12 15l5.25-5.25" },
    ],
  },
  "sys.menu": {
    name: "Menu",
    vb: 24,
    sizes: [18],
    paths: [
      { d: "M4.75 6.9h14.5" },
      { d: "M4.75 12h14.5" },
      { d: "M4.75 17.1h14.5" },
    ],
  },
  "sys.person": {
    name: "Person",
    vb: 24,
    sizes: [14, 20],
    paths: [
      { d: "M8.65 8.1a3.35 3.35 0 1 0 6.7 0a3.35 3.35 0 1 0 -6.7 0" },
      { d: "M4.9 19.5v-.65a5.6 5.6 0 0 1 5.6-5.6h3a5.6 5.6 0 0 1 5.6 5.6v.65" },
    ],
  },
  "sys.play": {
    name: "Play, open",
    vb: 24,
    sizes: [14, 16],
    paths: [
      { d: "M8.4 5.8v12.4a.6.6 0 0 0 .92.51l9.55-6.2a.6.6 0 0 0 0-1.02L9.32 5.29a.6.6 0 0 0-.92.51z" },
    ],
  },
  "sys.pause": {
    name: "Pause",
    vb: 24,
    sizes: [16],
    paths: [
      { d: "M9.15 6.4v11.2" },
      { d: "M14.85 6.4v11.2" },
    ],
  },
  "sys.lock": {
    name: "Lock",
    vb: 24,
    sizes: [15, 16],
    paths: [
      { d: "M7.1 11.15h9.8a1.65 1.65 0 0 1 1.65 1.65v5.3a1.75 1.75 0 0 1-1.75 1.75H7.2a1.75 1.75 0 0 1-1.75-1.75v-5.3a1.65 1.65 0 0 1 1.65-1.65z" },
      { d: "M8.65 11.15V8.4a3.35 3.35 0 0 1 6.7 0v2.75" },
      { d: "M10.75 15.5a1.25 1.25 0 1 0 2.5 0a1.25 1.25 0 1 0 -2.5 0", f: 1 },
    ],
  },
  "sys.slash": {
    name: "Muted",
    vb: 24,
    sizes: [14, 16],
    paths: [
      { d: "M4.25 12a7.75 7.75 0 1 0 15.5 0a7.75 7.75 0 1 0 -15.5 0" },
      { d: "M6.6 17.4 17.4 6.6" },
    ],
  },
  "sys.minus": {
    name: "Minus",
    vb: 24,
    sizes: [15, 18],
    paths: [
      { d: "M5.4 12h13.2" },
    ],
  },
  "sys.file": {
    name: "File",
    vb: 24,
    sizes: [16, 20],
    paths: [
      { d: "M7.25 3.75h5.9l5.6 5.6v9.15a1.75 1.75 0 0 1-1.75 1.75H7.25A1.75 1.75 0 0 1 5.5 18.5V5.5a1.75 1.75 0 0 1 1.75-1.75z" },
      { d: "M13.15 3.75V9.35h5.6" },
    ],
  },
  "sys.drop": {
    name: "Drop in",
    vb: 24,
    sizes: [20, 24],
    paths: [
      { d: "M12 4.25v8.6" },
      { d: "M8.9 9.75 12 12.85l3.1-3.1" },
      { d: "M4.75 15.4v2.85A1.75 1.75 0 0 0 6.5 20h11a1.75 1.75 0 0 0 1.75-1.75V15.4" },
    ],
  },
  "sys.share": {
    name: "Share up",
    vb: 24,
    sizes: [15, 17, 18],
    paths: [
      { d: "M12 14.4V4.4" },
      { d: "M8.9 7.5 12 4.4l3.1 3.1" },
      { d: "M15.1 11.15h1.9a1.6 1.6 0 0 1 1.6 1.6v5.5a1.75 1.75 0 0 1-1.75 1.75H7.15A1.75 1.75 0 0 1 5.4 18.25v-5.5a1.6 1.6 0 0 1 1.6-1.6h1.9" },
    ],
  },
  "sys.route": {
    name: "Switchback",
    vb: 24,
    sizes: [12, 16],
    paths: [
      { d: "M3.9000000000000004 18.4a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0 -3 0", f: 1 },
      { d: "M5.4 18.4h7.3a2.55 2.55 0 0 0 0-5.1H9.15a2.55 2.55 0 0 1 0-5.1h7.35" },
      { d: "M16.75 8.2a1.85 1.85 0 1 0 3.7 0a1.85 1.85 0 1 0 -3.7 0" },
    ],
  },
  "sys.locate": {
    name: "Locate",
    vb: 24,
    sizes: [16, 20],
    paths: [
      { d: "M6.4 12a5.6 5.6 0 1 0 11.2 0a5.6 5.6 0 1 0 -11.2 0" },
      { d: "M12 3.4v2.85" },
      { d: "M12 17.75v2.85" },
      { d: "M3.4 12h2.85" },
      { d: "M17.75 12h2.85" },
      { d: "M10.6 12a1.4 1.4 0 1 0 2.8 0a1.4 1.4 0 1 0 -2.8 0", f: 1 },
    ],
  },
  "sys.pin-off": {
    name: "Pin, off",
    vb: 24,
    sizes: [14, 16],
    paths: [
      { d: "M12 20.9s-6.6-5.35-6.6-10.15a6.6 6.6 0 0 1 13.2 0C18.6 15.55 12 20.9 12 20.9z" },
      { d: "M9.75 10.55a2.25 2.25 0 1 0 4.5 0a2.25 2.25 0 1 0 -4.5 0" },
      { d: "M4.6 4.6l14.8 14.8" },
    ],
  },
  "sys.arrow": {
    name: "Arrow",
    vb: 24,
    sizes: [14, 16],
    paths: [
      { d: "M4.75 12h14.5" },
      { d: "M13.55 6.35 19.25 12l-5.7 5.65" },
    ],
  },
  "sys.vitals": {
    name: "Pulse",
    vb: 24,
    sizes: [13, 16],
    paths: [
      { d: "M3.9 12.4h3.15l2-4.4 3.5 8.8 2-4.4h5.55" },
    ],
  },
  "sys.sync": {
    name: "Sync",
    vb: 24,
    sizes: [13, 16],
    paths: [
      { d: "M19.25 12a7.25 7.25 0 0 1-12.4 5.1" },
      { d: "M6 21.05v-3.9h3.9" },
      { d: "M4.75 12a7.25 7.25 0 0 1 12.4-5.1" },
      { d: "M18 2.95v3.9h-3.9" },
    ],
  },
  "sys.wearable": {
    name: "Watch",
    vb: 24,
    sizes: [13, 16],
    paths: [
      { d: "M7.1 12a4.9 4.9 0 1 0 9.8 0a4.9 4.9 0 1 0 -9.8 0" },
      { d: "M12 9.95V12l1.55.9" },
      { d: "M9.2 7.55l-.5-3.55h6.6l-.5 3.55" },
      { d: "M9.2 16.45l-.5 3.55h6.6l-.5-3.55" },
    ],
  },
  "sys.weather": {
    name: "Sun & cloud",
    vb: 24,
    sizes: [14, 16],
    paths: [
      { d: "M6.6 9.9a3 3 0 1 1 5.6 1" },
      { d: "M9.55 3.4v1.7" },
      { d: "M4.05 8.9h1.7" },
      { d: "M5.65 5 6.85 6.2" },
      { d: "M9.15 19.4h7.75a3.1 3.1 0 0 0 .6-6.15 4.55 4.55 0 0 0-8.85 1 2.8 2.8 0 0 0 .5 5.15z" },
    ],
  },
  "sys.shield-care": {
    name: "Sheltered",
    vb: 24,
    sizes: [16, 20],
    paths: [
      { d: "M4.75 13.3a7.25 7.25 0 0 1 14.5 0" },
      { d: "M10.45 13.3a1.55 1.55 0 1 0 3.1 0a1.55 1.55 0 1 0 -3.1 0", f: 1 },
      { d: "M8.85 19.3a3.15 3.15 0 0 1 6.3 0" },
    ],
  },
  "sys.medical": {
    name: "Cross",
    vb: 24,
    sizes: [16, 20],
    paths: [
      { d: "M9.2 4.75h5.6v4.45h4.45v5.6H14.8v4.45H9.2V14.8H4.75V9.2H9.2z" },
    ],
  },
  "chip.pr": {
    name: "New height",
    vb: 24,
    sizes: [12, 16],
    paths: [
      { d: "M5.5 18.75h13" },
      { d: "M12 15.1V6.1" },
      { d: "M8.9 9.2 12 6.1l3.1 3.1" },
    ],
  },
  "sys.dot": {
    name: "Micro-dot",
    vb: 24,
    sizes: [3, 4],
    paths: [
      { d: "M10.5 12a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0 -3 0", f: 1 },
    ],
  },
  "chip.goal": {
    name: "Goal race",
    vb: 24,
    sizes: [14, 16],
    paths: [
      { d: "M12 3.75A8.25 8.25 0 1 0 12 20.25A8.25 8.25 0 1 0 12 3.75Z" },
      { d: "M12 7.75A4.25 4.25 0 1 0 12 16.25A4.25 4.25 0 1 0 12 7.75Z" },
      { d: "M12 10.85A1.15 1.15 0 1 0 12 13.15A1.15 1.15 0 1 0 12 10.85Z" },
    ],
  },
  "chip.important": {
    name: "Important",
    vb: 24,
    sizes: [14, 16],
    paths: [
      { d: "M12 3.25L20.75 12L12 20.75L3.25 12Z" },
    ],
  },
  "wx.clear": {
    name: "Sun",
    vb: 24,
    sizes: [14, 16],
    paths: [
      { d: "M8.4 12a3.6 3.6 0 1 0 7.2 0a3.6 3.6 0 1 0 -7.2 0" },
      { d: "M12 4.4v2.2" },
      { d: "M12 17.4v2.2" },
      { d: "M4.4 12h2.2" },
      { d: "M17.4 12h2.2" },
      { d: "M6.6 6.6l1.55 1.55" },
      { d: "M15.85 15.85l1.55 1.55" },
      { d: "M17.4 6.6l-1.55 1.55" },
      { d: "M8.15 15.85 6.6 17.4" },
    ],
  },
  "wx.partly": {
    name: "Sun & cloud (alias)",
    vb: 24,
    sizes: [14, 16],
    paths: [
      { d: "M6.6 9.9a3 3 0 1 1 5.6 1" },
      { d: "M9.55 3.4v1.7" },
      { d: "M4.05 8.9h1.7" },
      { d: "M5.65 5 6.85 6.2" },
      { d: "M9.15 19.4h7.75a3.1 3.1 0 0 0 .6-6.15 4.55 4.55 0 0 0-8.85 1 2.8 2.8 0 0 0 .5 5.15z" },
    ],
  },
  "wx.overcast": {
    name: "Cloud",
    vb: 24,
    sizes: [14, 16],
    paths: [
      { d: "M9.15 16.4h7.75a3.1 3.1 0 0 0 .6-6.15 4.55 4.55 0 0 0-8.85 1 2.8 2.8 0 0 0 .5 5.15z" },
    ],
  },
  "wx.fog": {
    name: "Fog",
    vb: 24,
    sizes: [14, 16],
    paths: [
      { d: "M9.15 16.4h7.75a3.1 3.1 0 0 0 .6-6.15 4.55 4.55 0 0 0-8.85 1 2.8 2.8 0 0 0 .5 5.15z" },
      { d: "M6.5 19.4h9.5" },
      { d: "M8.5 21.6h7" },
    ],
  },
  "wx.rain": {
    name: "Rain",
    vb: 24,
    sizes: [14, 16],
    paths: [
      { d: "M9.15 16.4h7.75a3.1 3.1 0 0 0 .6-6.15 4.55 4.55 0 0 0-8.85 1 2.8 2.8 0 0 0 .5 5.15z" },
      { d: "M9.4 18.4l-.6 1.8" },
      { d: "M12.6 18.4l-.6 1.8" },
      { d: "M15.8 18.4l-.6 1.8" },
    ],
  },
  "wx.snow": {
    name: "Snow",
    vb: 24,
    sizes: [14, 16],
    paths: [
      { d: "M9.15 16.4h7.75a3.1 3.1 0 0 0 .6-6.15 4.55 4.55 0 0 0-8.85 1 2.8 2.8 0 0 0 .5 5.15z" },
      { d: "M8.5 19.3a.9 .9 0 1 0 1.8 0a.9 .9 0 1 0 -1.8 0", f: 1 },
      { d: "M11.7 20.9a.9 .9 0 1 0 1.8 0a.9 .9 0 1 0 -1.8 0", f: 1 },
      { d: "M14.9 19.3a.9 .9 0 1 0 1.8 0a.9 .9 0 1 0 -1.8 0", f: 1 },
    ],
  },
  "wx.storm": {
    name: "Thunderstorm",
    vb: 24,
    sizes: [14, 16],
    paths: [
      { d: "M9.15 16.4h7.75a3.1 3.1 0 0 0 .6-6.15 4.55 4.55 0 0 0-8.85 1 2.8 2.8 0 0 0 .5 5.15z" },
      { d: "M13 15.6l-2.2 3.1h2l-1.5 2.7" },
    ],
  },
  "nav.week": {
    name: "Week",
    vb: 24,
    sizes: [22, 24],
    paths: [
      { d: "M5.5 5h13a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z" },
      { d: "M3.5 10h17" },
      { d: "M8 3v4" },
      { d: "M16 3v4" },
      { d: "M7 17.5l3.5-3l2.5 2l4-4" },
    ],
  },
} as const;

/** Every ratified icon name. */
export type IconName = keyof typeof ICONS_RAW;

/**
 * The registry's art, as data.
 *
 * ⚠️ Exported through an `IconDef` annotation rather than raw `as const`: the
 * const type narrows each entry to its OWN literal shape, so `ICONS[name].stroke`
 * and `path.f` stop existing on the ~30 entries that omit them and every generic
 * consumer fails to compile. `ICONS_RAW` keeps the literal KEYS (which is what
 * `IconName` needs); this view gives every entry the full optional shape.
 */
export const ICONS: Record<IconName, IconDef> = ICONS_RAW;

/** All 98 names, in ratification order. */
export const ICON_NAMES = Object.keys(ICONS_RAW) as IconName[];

/**
 * icons.json#kudoWireMap — the FROZEN `kudoTypes.ts` wire ids on the left,
 * display art on the right. The ids, their ordering and their payloads are
 * untouched by this lane (README §Kudos); only the art resolves through here.
 */
export const KUDO_WIRE_MAP = {
  "fire": "kudo.fire",
  "beast": "kudo.beast",
  "trophy": "kudo.trophy",
  "star": "kudo.star",
  "heart": "kudo.heart",
  "lightning": "kudo.lightning",
} as const satisfies Record<string, IconName>;

/** Direction defaults (icons.json#direction). */
export const STROKE_DEFAULT = 1.5;

/**
 * ⛔ RETIRED BY R1 (CD-ICONS-02, founder-ratified B — "badge only").
 *
 * The ratified unread dot NEVER SHIPS. Unread is the numeric badge, filled
 * `var(--signal)` — so `--signal` still has exactly one instance in the app,
 * it is just the badge background rather than a glyph mark. The
 * approvals-strip bell swaps with NO unread affordance at all.
 *
 * The geometry is kept as a NAMED TOMBSTONE rather than deleted: the fidelity
 * suite asserts the registry emits no such circle, and an assertion that
 * something is absent reads better against the number it must not equal.
 */
export const RETIRED_NOTIFY_DOT_R1 = { cx: 18.6, cy: 5.2, r: 1.9 } as const;

/** `mark.celebrate` draw-out budget — README caps it at 450ms, once. */
export const CELEBRATE_DURATION_MS = 450;
